import { CustomerProperties } from "@api/customer";
import {
  ConditionKey,
  EventKey,
  EventPropertyOption,
  IntegrationProviderId,
  ListRule,
  PropertyType,
} from "@api/types";
import { startCase } from "lodash";
import { AvailableCustomerEvent } from "@/api/customer-event";
import { INTEGRATION_PROVIDERS } from "./integrations";

export const EMPTY_OPERATOR = {
  op: "",
  value: undefined,
};
export const EMPTY_COUNT_OPERATOR = {
  op: "$exists",
  value: 1,
};
export const EMPTY_CONDITION = {
  key: "",
  type: "",
  operators: [EMPTY_OPERATOR],
};
export const EMPTY_EVENT = {
  event: "",
  type: "",
  count: EMPTY_COUNT_OPERATOR,
  conditions: [],
};
export const EMPTY_CONDITION_RULE: ListRule = {
  type: "condition",
  rule: EMPTY_CONDITION,
};
export const EMPTY_EVENT_RULE: ListRule = {
  type: "event",
  rule: EMPTY_EVENT,
};
export const EMPTY_INITIAL_VALUES = {
  name: "",
  rules: [EMPTY_CONDITION_RULE],
};
export const EMPTY_PREVIEW = {
  count: 0,
  members: [],
};
export const RULE_TYPES = [
  {
    label: "Properties about someone",
    value: "condition",
  },
  {
    label: "What someone has done",
    value: "event",
  },
  // {
  //   name: 'If someone is or is not in a list',
  //   id: 'list'
  // }
];

export const EVENT_COUNT_OPERATORS = [
  {
    label: "At least once",
    value: "$exists",
  },
  {
    label: "Exactly",
    value: "$eq",
  },
  {
    label: "Greater than",
    value: "$gt",
  },
  {
    label: "Greater than or equal to",
    value: "$gte",
  },
  {
    label: "Less than",
    value: "$lt",
  },
  {
    label: "Less than or equal to",
    value: "$lte",
  },
];

export const STRING_OPERATORS = [
  {
    label: "Exists",
    value: "$exists",
  },
  {
    label: "Contains",
    value: "$regex",
  },
  {
    label: "Equals",
    value: "$eq",
  },
  {
    label: "Does not equal",
    value: "$ne",
  },
  {
    label: "Includes any",
    value: "$in",
  },
  {
    label: "Does not include any",
    value: "$nin",
  },
];
export const NUMBER_OPERATORS = [
  {
    label: "Equals",
    value: "$eq",
  },
  {
    label: "Exists",
    value: "$exists",
  },
  {
    label: "Does not equal",
    value: "$ne",
  },
  {
    label: "Less than",
    value: "$lt",
  },
  {
    label: "Less than or equal to",
    value: "$lte",
  },
  {
    label: "Greater than",
    value: "$gt",
  },
  {
    label: "Greater than or equal to",
    value: "$gte",
  },
];
export const CURRENCY_OPERATORS = [
  {
    label: "Less than",
    value: "$lt",
  },
  {
    label: "Less than or equal to",
    value: "$lte",
  },
  {
    label: "Greater than",
    value: "$gt",
  },
  {
    label: "Greater than or equal to",
    value: "$gte",
  },
];
/** Customer-event timestamp (Mongo `createdAt`), not class/session start time. */
export const EVENT_OCCURRED_AT_PROPERTY_KEY = "createdAt";
export const EVENT_OCCURRED_AT_PROPERTY_LABEL = "When event occurred";

export const RELATIVE_DATE_OPERATOR_OPS = ["$last", "$next", "$exactLast", "$exactNext"] as const;

export function isRelativeDateOperator(op: string | undefined): boolean {
  return RELATIVE_DATE_OPERATOR_OPS.includes(op as (typeof RELATIVE_DATE_OPERATOR_OPS)[number]);
}

export const DATE_OPERATORS = [
  {
    label: "Exists",
    value: "$exists",
  },
  {
    label: "In the last",
    value: "$last",
  },
  {
    label: "In the next",
    value: "$next",
  },
  {
    label: "Exactly … ago",
    value: "$exactLast",
  },
  {
    label: "In exactly …",
    value: "$exactNext",
  },
  {
    label: "Before",
    value: "$lte",
  },
  {
    label: "After",
    value: "$gte",
  },
];
export const ORDINAL_OPERATORS = [
  {
    label: "In the next",
    value: "$next",
  },
  {
    label: "In the last",
    value: "$last",
  },
];
export const BOOLEAN_OPERATORS = [
  {
    label: "Exists",
    value: "$exists",
  },
  {
    label: "Equals",
    value: "$eq",
  },
  {
    label: "Does not equal",
    value: "$ne",
  },
];
export const BOOLEAN_VALUES = [
  {
    label: "True",
    value: "true",
  },
  {
    label: "False",
    value: "false",
  },
];
export const ENUM_OPERATORS = [
  {
    label: "Equals",
    value: "$eq",
  },
  {
    label: "Does not equal",
    value: "$ne",
  },
];
export const ARRAY_OPERATORS = [
  {
    label: "Includes any",
    value: "$in",
  },
  {
    label: "Does not include any",
    value: "$nin",
  },
];
export const ENUM_VALUES: { [key: string]: any[] } = {
  duration: [
    {
      label: "Hours",
      value: "hours",
    },
    {
      label: "Days",
      value: "days",
    },
    {
      label: "Weeks",
      value: "weeks",
    },
    {
      label: "Months",
      value: "months",
    },
    {
      label: "Years",
      value: "years",
    },
  ],
  "properties.mindbody.Gender": [
    {
      name: "Male",
      id: "Male",
    },
    {
      name: "Female",
      id: "Female",
    },
  ],
  "properties.mindbody.Status": [
    {
      name: "Active",
      id: "Active",
    },
    {
      name: "Non member",
      id: "Non-Member",
    },
    {
      name: "Expired",
      id: "Expired",
    },
    {
      name: "Declined",
      id: "Declined",
    },
    {
      name: "Suspended",
      id: "Suspended",
    },
    {
      name: "Terminated",
      id: "Terminated",
    },
  ],
  "properties.mindbody.MembershipStats.AutopayStatus": [
    {
      name: "Active",
      id: "Active",
    },
    {
      name: "Inactive",
      id: "Inactive",
    },
    {
      name: "Suspended",
      id: "Suspended",
    },
  ],
};
export const OPERATORS: { [key: string]: any[] } = {
  string: STRING_OPERATORS,
  number: NUMBER_OPERATORS,
  currency: CURRENCY_OPERATORS,
  date: DATE_OPERATORS,
  ordinal: ORDINAL_OPERATORS,
  boolean: BOOLEAN_OPERATORS,
  enum: ENUM_OPERATORS,
  array: ARRAY_OPERATORS,
};
export const OPERATOR_VALUES = {};

export type GroupedOption<T> = {
  label: string;
  options: T[];
};
/**
 * Marketer-facing labels for customer-property keys whose `startCase`
 * derivation reads as API jargon in the "All fields" list (e.g.
 * `properties.fitdegree.passes.title` → "Passes Title"). FitDEGREE calls
 * class packs "passes" and products "purchasables"; marketers don't.
 */
export const CUSTOMER_PROPERTY_LABEL_OVERRIDES: Record<string, string> = {
  // FitDEGREE — journey & spend
  "properties.fitdegree.created_at": "Sign-up date",
  "properties.fitdegree.first_class_date": "First class date",
  "properties.fitdegree.last_class_taken_date": "Last class date",
  "properties.fitdegree.next_class_date": "Next class date",
  "properties.fitdegree.total_spend_cents": "Lifetime spend",
  "properties.fitdegree.total_refunded_cents": "Total refunded",
  "properties.fitdegree.last_purchase_at": "Last purchase date",
  // FitDEGREE — purchases
  "properties.fitdegree.purchased_names": "Products purchased",
  "properties.fitdegree.purchases.name": "Purchase item name",
  "properties.fitdegree.purchases.type": "Purchase item type",
  "properties.fitdegree.purchases.purchase_date": "Purchase date",
  // FitDEGREE — class packs ("passes" in the API)
  "properties.fitdegree.passes.title": "Class pack name",
  "properties.fitdegree.passes.status": "Class pack status",
  "properties.fitdegree.passes.expiration_date": "Class pack expiration date",
  "properties.fitdegree.passes.remaining_quantity": "Classes remaining on pack",
  "properties.fitdegree.passes.original_quantity": "Class pack size",
  "properties.fitdegree.active_pass_count": "Active class packs",
  "properties.fitdegree.pass_classes_remaining": "Classes remaining",
  // FitDEGREE — memberships & intro offers
  "properties.fitdegree.memberships.title": "Membership name",
  "properties.fitdegree.memberships.status": "Membership status",
  "properties.fitdegree.memberships.start_at": "Membership start date",
  "properties.fitdegree.memberships.end_at": "Membership end date",
  "properties.fitdegree.memberships.autopay_enabled": "Membership autopay",
  "properties.fitdegree.memberships.payment_mode": "Membership payment mode",
  "properties.fitdegree.offers.title": "Intro offer name",
  "properties.fitdegree.offers.status": "Intro offer status",
  "properties.fitdegree.offers.expiration_date": "Intro offer expiration date",
  // FitDEGREE — visits
  "properties.fitdegree.attended_class_names": "Classes attended",
  "properties.fitdegree.attended_instructor_names": "Instructors attended",
  // Mindbody — normalized rollups
  "properties.mindbody.attended_class_count": "Total visits",
  "properties.mindbody.first_attended_at": "First visit",
  "properties.mindbody.last_attended_at": "Last visit",
  "properties.mindbody.next_class_at": "Next class scheduled",
  "properties.mindbody.booked_class_count": "Total bookings",
  "properties.mindbody.late_cancelled_class_count": "Late cancellations",
  "properties.mindbody.pass_sessions_remaining": "Sessions remaining",
  "properties.mindbody.active_pass_count": "Active class packs",
  "properties.mindbody.active_membership": "Active member",
  "properties.mindbody.active_membership_name": "Membership name",
  "properties.mindbody.annual_membership": "Annual member",
  "properties.mindbody.intro_offer_started_at": "Intro offer purchased on",
  "properties.mindbody.intro_offer_ends_at": "Intro offer ends on",
  "properties.mindbody.intro_converted": "Converted from intro",
  "properties.mindbody.intro_converted_at": "Intro converted on",
  "properties.mindbody.last_purchase_at": "Last purchase date",
  "properties.mindbody.Status": "Membership status",
  "properties.mindbody.MembershipStats.AutopayStatus": "Autopay status",
  "properties.mindbody.MembershipStats.EndDate": "Membership end date",
  // Mindbody — entitlement arrays
  "properties.mindbody.passes.name": "Class pack name",
  "properties.mindbody.passes.remaining": "Sessions remaining on pack",
  "properties.mindbody.passes.count": "Class pack size",
  "properties.mindbody.passes.expiration_date": "Class pack expiration date",
  "properties.mindbody.passes.active_date": "Class pack start date",
  "properties.mindbody.passes.intro_offer": "Intro offer pack",
  "properties.mindbody.memberships.name": "Membership name",
  "properties.mindbody.memberships.active_date": "Membership start date",
  "properties.mindbody.memberships.expiration_date": "Membership expiration date",
  "properties.mindbody.contracts.name": "Contract name",
  "properties.mindbody.contracts.start_date": "Contract start date",
  "properties.mindbody.contracts.end_date": "Contract end date",
  "properties.mindbody.contracts.termination_date": "Contract termination date",
  "properties.mindbody.contracts.autopay_status": "Autopay status",
  "properties.mindbody.purchases.name": "Purchase item name",
  // Zenoti guest snapshot
  "properties.zenoti.id": "Zenoti guest ID",
  "properties.zenoti.code": "Guest code",
  "properties.zenoti.center_id": "Home center ID",
  "properties.zenoti.center_name": "Home center",
  "properties.zenoti.created_date": "Guest since",
  "properties.zenoti.member_since": "Member since",
  "properties.zenoti.date_of_birth": "Date of birth",
  "properties.zenoti.date_of_birth_ordinal": "Birthday",
  "properties.zenoti.gender": "Gender",
  "properties.zenoti.preferences.receive_marketing_sms": "Opted in to marketing texts",
  "properties.zenoti.preferences.receive_transactional_sms": "Opted in to appointment texts",
  "properties.zenoti.preferences.receive_marketing_email": "Opted in to marketing email",
  "properties.zenoti.preferences.receive_transactional_email": "Opted in to appointment email",
  "properties.zenoti.synced_at": "Last synced from Zenoti",
  // Zenoti membership rollups
  "properties.zenoti.is_member": "Member",
  "properties.zenoti.membership_name": "Membership plan",
  "properties.zenoti.membership_status": "Membership status",
  "properties.zenoti.membership_type": "Membership type",
  "properties.zenoti.membership_started_at": "Membership started",
  "properties.zenoti.membership_expires_at": "Membership expires",
  "properties.zenoti.membership_next_billing_at": "Next billing date",
  "properties.zenoti.membership_price": "Membership price",
  "properties.zenoti.membership_cancelled_at": "Membership cancelled on",
  "properties.zenoti.membership_credits_left": "Credits left",
  "properties.zenoti.membership_credits_expire_at": "Credits expire",
  "properties.zenoti.became_member_at": "First became a member",
  // Zenoti visit rollups
  "properties.zenoti.visit_count": "Visits",
  "properties.zenoti.first_visit_at": "First visit",
  "properties.zenoti.last_visit_at": "Last visit",
  "properties.zenoti.last_visit_service": "Last visit service",
  "properties.zenoti.last_visit_therapist": "Last visit therapist",
  "properties.zenoti.next_appointment_at": "Next appointment",
  "properties.zenoti.next_appointment_service": "Next appointment service",
  "properties.zenoti.next_appointment_therapist": "Next appointment therapist",
  "properties.zenoti.upcoming_appointment_count": "Upcoming appointments",
  "properties.zenoti.no_show_count": "No-shows",
  "properties.zenoti.last_no_show_at": "Last no-show",
  "properties.zenoti.cancelled_count": "Cancellations",
  "properties.zenoti.top_service": "Top service",
  "properties.zenoti.preferred_therapist": "Preferred therapist",
  "properties.zenoti.rebooked_count": "Rebooked visits",
  "properties.zenoti.addon_visit_count": "Visits with add-ons",
  // Zenoti package and spend rollups
  "properties.zenoti.package_count": "Active packages",
  "properties.zenoti.package_sessions_left": "Package sessions left",
  "properties.zenoti.package_expires_at": "Package expires",
  "properties.zenoti.package_names": "Package names",
  "properties.zenoti.lifetime_spend": "Lifetime spend",
};

export function parseCustomerPropertyLabel(key: string): string {
  const override = CUSTOMER_PROPERTY_LABEL_OVERRIDES[key];
  if (override) return override;
  let label: string = key;
  label = label.replace("properties.tablelist.stats", "");
  label = label.replace("properties.tablelist.socialProfiles", "");
  label = label.replace("properties.mindbody.VisitStats", "");
  label = label.replace("properties.mariana_tek", "");
  INTEGRATION_PROVIDERS.forEach((integration) => {
    label = label.replace(`properties.${integration.id.replace("-", "")}.`, "");
  });
  label = label.replace(`properties.`, "");
  return startCase(label);
}

export function parseCustomerPropertySource(key: string): IntegrationProviderId {
  if (key.includes("properties.mindbody")) {
    return "mindbody";
  } else if (key.includes("properties.activecampaign")) {
    return "activecampaign";
  } else if (key.includes("properties.tablelist")) {
    return "tablelist";
  } else if (key.includes("properties.fitdegree")) {
    return "fitdegree";
  } else if (key.includes("properties.mariana_tek")) {
    return "mariana-tek";
  } else if (key.includes("properties.zenoti")) {
    return "zenoti";
  } else {
    return "helios";
  }
}

const EVENT_PROPERTY_INTERNAL_TOKENS = [
  "raw",
  "payload",
  "request",
  "response",
  "webhook",
  "signature",
  "secret",
  "token",
];

/**
 * Provider-specific event property curation. These labels are intentionally
 * grounded in the public concepts exposed by the providers' docs (fitDEGREE
 * data objects / Mariana Tek admin resources) while still accepting sampled
 * properties from our API. The dictionaries fix the worst raw integration
 * names first; the whitelist keeps obvious internal/webhook fields out of
 * the segment UI.
 */
const EVENT_PROPERTY_LABELS_BY_PROVIDER: Record<string, Record<string, string>> = {
  "mariana-tek": {
    [EVENT_OCCURRED_AT_PROPERTY_KEY]: EVENT_OCCURRED_AT_PROPERTY_LABEL,
    status: "Status",
    type: "Type",
    name: "Name",
    id: "ID",
    "class_session.class_name": "Class name",
    "class_session.class_type.duration": "Duration",
    "class_session.class_type.is_live_stream": "Live stream",
    "class_session.class_type.name": "Class type",
    "class_session.classroom_name": "Room",
    "class_session.end_datetime": "Class end date/time",
    "class_session.instructors.name": "Instructor",
    "class_session.is_free_class": "Free class",
    "class_session.location.name": "Location",
    "class_session.public": "Public class",
    "class_session.spot": "Spot",
    "class_session.start_date": "Class date",
    "class_session.start_datetime": "Class start date/time",
    "customer.home_location.name": "Home location",
    "customer.id": "Customer ID",
    "customer.name": "Customer name",
    "location.name": "Location",
    number: "Order number",
    order_datetime: "Order date/time",
    "order_lines.product.name": "Product",
    "order_lines.product.sku": "SKU",
    purchase_date: "Purchase date",
    purchase_location: "Purchase location",
    start_date: "Start date",
    end_date: "End date",
    subtotal: "Subtotal",
    total: "Total",
    currency: "Currency",
    "registrable_data.class_id": "Class ID",
    "registrable_data.class_name": "Class name",
    "registrable_data.class_type": "Class type",
    "registrable_data.location_id": "Location ID",
    "registrable_data.location_name": "Location",
    "registrable_data.room_id": "Room ID",
    "registrable_data.room_name": "Room",
    "registrable_data.staff_id": "Instructor ID",
    "registrable_data.staff_name": "Instructor",
    "consumable_data.autopay_enabled": "Autopay enabled",
    "consumable_data.classpass_reservation_id": "ClassPass reservation ID",
    "consumable_data.completion_datetime": "Completion date/time",
    "consumable_data.start_datetime": "Start date/time",
    "consumable_data.payment_mode": "Payment mode",
    "consumable_data.frozen.is_frozen": "Frozen",
    "consumable_data.frozen.freeze_datetime": "Freeze date/time",
    "consumable_data.frozen.unfreeze_datetime": "Unfreeze date/time",
  },
  fitdegree: {
    [EVENT_OCCURRED_AT_PROPERTY_KEY]: EVENT_OCCURRED_AT_PROPERTY_LABEL,
    id: "ID",
    status: "Status",
    type: "Type",
    title: "Title",
    instructor_name: "Instructor",
    purchased_names: "Products purchased",
    creation_datetime: "Created date/time",
    last_updated_datetime: "Last updated date/time",
    user_id: "Member ID",
    fitspot_id: "Location ID",
    "member.id": "Member ID",
    "member.first_name": "First name",
    "member.last_name": "Last name",
    "member.email": "Email",
    "member.phone": "Phone",
    "event.id": "Event ID",
    "event.name": "Event name",
    "event.start_time": "Start time",
    "event.end_time": "End time",
    "class.id": "Class ID",
    "class.name": "Class name",
    "class.title": "Class name",
    "class.group_id": "Class group ID",
    "registration.id": "Registration ID",
    "registration.status": "Registration status",
    registration_id: "Registration ID",
    registrable_id: "Registrable ID",
    late_cancelled: "Late cancelled",
    "registrable.id": "Registrable ID",
    "registrable.title": "Class name",
    "registrable.type": "Type",
    "registrable.class_id": "Class ID",
    "registrable.fitspot_id": "Location ID",
    "registrable.start_datetime": "Class start date/time",
    "registrable.end_datetime": "Class end date/time",
    "registrable.is_cancelled": "Class cancelled",
    "membership.id": "Membership ID",
    "membership.name": "Membership name",
    "invoice.id": "Invoice ID",
    "payment.mode": "Payment mode",
    "payment.status": "Payment status",
    sale_id: "Sale ID",
    "sale.id": "Sale ID",
    "sale.creation_datetime": "Sale date/time",
    "sale.last_updated_datetime": "Sale updated date/time",
    "sale_items.id": "Sale item ID",
    "sale_items.paid_price": "Paid amount",
    "sale_items.refunded_amount": "Refunded amount",
    refunded_amount_cents: "Refunded amount",
    "sale_items.purchasable_id": "Purchasable ID",
    "sale_items.consumable_id": "Consumable ID",
    "purchasable.id": "Purchasable ID",
    "purchasable.title": "Product name",
    "purchasable.name": "Product name",
    "purchasable.type": "Product type",
    "purchasables.id": "Purchasable ID",
    "purchasables.title": "Product name",
    "purchasables.name": "Product name",
    "purchasables.type": "Product type",
    // "Consumable" is FitDEGREE API jargon for an owned item (class pack,
    // membership, intro offer) — marketers see "Item …" / class-pack terms.
    "consumable.id": "Item ID",
    "consumable.type": "Item type",
    "consumable.title": "Item name",
    "consumable.purchasable_id": "Purchasable ID",
    "consumable.activation_datetime": "Activation date/time",
    "consumable.autopay_enabled": "Autopay enabled",
    "consumable.completion_datetime": "Completion date/time",
    "consumable.payment_mode": "Payment mode",
    "consumable.remaining_quantity": "Classes remaining",
    "consumable.data.expiration_date": "Expiration date",
    "consumable.data.original_quantity": "Class pack size",
    "consumable.data.remaining_quantity": "Classes remaining",
    "consumable.data.classpass_reservation_id": "ClassPass reservation ID",
    "consumables.id": "Item ID",
    "consumables.type": "Item type",
    "consumables.title": "Item name",
    "consumables.purchasable_id": "Purchasable ID",
    "consumables.activation_datetime": "Activation date/time",
    "consumables.autopay_enabled": "Autopay enabled",
    "consumables.completion_datetime": "Completion date/time",
    "consumables.payment_mode": "Payment mode",
    "consumables.remaining_quantity": "Classes remaining",
    "consumables.data.expiration_date": "Expiration date",
    "consumables.data.original_quantity": "Class pack size",
    "consumables.data.remaining_quantity": "Classes remaining",
    "consumables.data.classpass_reservation_id": "ClassPass reservation ID",
    "fitspot.id": "Location ID",
    "fitspot.name": "Location",
    "fitspot.time_zone": "Time zone",
    "fitspot.email_address": "Location email",
    "fitspot.phone_number": "Location phone",
    "fitspot.address.city": "City",
    "fitspot.address.state": "State",
    "fitspot.address.zip": "ZIP code",
  },
  mindbody: {
    [EVENT_OCCURRED_AT_PROPERTY_KEY]: EVENT_OCCURRED_AT_PROPERTY_LABEL,
    // Class bookings and outcomes
    className: "Class name",
    classStartDateTime: "Class start",
    classEndDateTime: "Class end",
    classDateTime: "Class start",
    locationName: "Location",
    signedInStatus: "Check-in status",
    staffName: "Instructor",
    bookingOriginatedFromWaitlist: "Booked from waitlist",
    clientsNumberOfVisitsAtSite: "Visits at this location",
    clientPassSessionsTotal: "Class pack size",
    clientPassSessionsRemaining: "Sessions remaining on pack",
    clientPassActivationDateTime: "Class pack start",
    clientPassExpirationDateTime: "Class pack expiration",
    itemName: "Pricing option",
    // Purchases
    soldByName: "Sold by",
    totalAmountPaid: "Amount paid",
    saleDateTime: "Sale date",
    "items.name": "Product name",
    "items.type": "Product type",
    "items.amount": "Amount",
    "items.amountPaid": "Amount paid",
    "items.quantity": "Quantity",
    // Appointments
    status: "Appointment status",
    isConfirmed: "Confirmed",
    hasArrived: "Arrived",
    staffFirstName: "Staff first name",
    staffLastName: "Staff last name",
    startDateTime: "Start time",
    endDateTime: "End time",
    durationMinutes: "Duration (minutes)",
    appointmentName: "Appointment",
    // IDs stay labeled for saved rules; the dropdown hides them.
    classId: "Class ID",
    locationId: "Location ID",
    itemId: "Product ID",
  },
  zenoti: {
    [EVENT_OCCURRED_AT_PROPERTY_KEY]: EVENT_OCCURRED_AT_PROPERTY_LABEL,
    // Appointments
    appointmentName: "Appointment",
    startDateTime: "Start time",
    endDateTime: "End time",
    services: "Services",
    addons: "Add-ons",
    therapist: "Therapist",
    room: "Room",
    price: "Price",
    isRebooking: "Rebooked",
    // Memberships
    membershipName: "Membership plan",
    startDate: "Start date",
    cancelledDate: "Cancelled date",
    // Packages
    packageName: "Package",
    sessions: "Sessions",
  },
  "helios-pixel": {
    [EVENT_OCCURRED_AT_PROPERTY_KEY]: EVENT_OCCURRED_AT_PROPERTY_LABEL,
    path: "Path",
    landingPage: "Landing page",
    form: "Form",
    formTitle: "Form title",
    item: "Catalog item",
    event: "Event",
    cta: "CTA",
    href: "Link",
    utm_source: "UTM source",
    utm_medium: "UTM medium",
    utm_campaign: "UTM campaign",
    utm_term: "UTM term",
    utm_content: "UTM content",
    providerUserId: "Provider user ID",
  },
};

const EVENT_PROPERTY_VALUE_LABELS_BY_PROVIDER: Record<
  string,
  Record<string, Record<string, string>>
> = {
  mindbody: {
    signedInStatus: {
      SignedIn: "Signed in",
      NotSignedIn: "Not signed in",
    },
    clientPassSessionsTotal: {
      "99999": "Unlimited",
    },
  },
};

/**
 * Keys hidden from the event-property dropdown for a provider even though
 * they carry labels above. The labels stay so already-saved rules keep
 * rendering their friendly names — this list only stops NEW selections.
 */
const EVENT_PROPERTY_EXCLUDED_KEYS_BY_PROVIDER: Record<string, Set<string>> = {
  fitdegree: new Set([
    // Duplicates of "Product name" / "Product type": a sale's consumable
    // rows mirror its purchasables, so surfacing both reads as two
    // identical filters. Prefer the purchasable-backed Product keys.
    "consumable.type",
    "consumable.title",
    "consumables.type",
    "consumables.title",
    // Location minutiae — "Location" (fitspot.name) stays; nobody segments
    // purchases by the gym's own email/phone/timezone/address parts.
    "fitspot.time_zone",
    "fitspot.email_address",
    "fitspot.phone_number",
    "fitspot.address.city",
    "fitspot.address.state",
    "fitspot.address.zip",
    // Raw record timestamps — "When event occurred" already covers time
    // filtering without exposing API record-keeping fields.
    "creation_datetime",
    "last_updated_datetime",
    // Customer-level rollup that leaks into sale events; the curated
    // "Products purchased" customer property is the right home for it.
    "purchased_names",
  ]),
  mindbody: new Set([
    // Opaque numeric IDs. camelCase `classId` / `locationId` also get
    // dropped by `isIdentifierEventProperty`; these stay so already-saved
    // rules keep a friendly label if someone selected them before.
    "classId",
    "locationId",
    "itemId",
    "itemSiteId",
    // Outcomes also write classStartDateTime; offering both start keys
    // would show "Class start" twice on new picks.
    "classDateTime",
  ]),
};

const EVENT_PROPERTY_ALLOWED_PREFIXES_BY_PROVIDER: Record<string, string[]> = {
  "mariana-tek": [
    "createdAt",
    "status",
    "type",
    "id",
    "name",
    "number",
    "currency",
    "subtotal",
    "total",
    "class_session",
    "customer",
    "registrable_data",
    "consumable_data",
    "reservation",
    "order_datetime",
    "order_lines",
    "order",
    "payment",
    "class",
    "location",
    "room",
    "staff",
    "membership",
    "purchase_date",
    "purchase_location",
    "start_date",
    "end_date",
  ],
  fitdegree: [
    "createdAt",
    "id",
    "status",
    "type",
    "title",
    "creation_datetime",
    "last_updated_datetime",
    "user_id",
    "fitspot_id",
    "registration_id",
    "registrable_id",
    "sale_id",
    "refunded_amount_cents",
    "late_cancelled",
  ],
  "helios-pixel": [
    "createdAt",
    "path",
    "landingPage",
    "form",
    "formTitle",
    "item",
    "event",
    "cta",
    "href",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "providerUserId",
  ],
  mindbody: [
    "createdAt",
    "className",
    "classStartDateTime",
    "classEndDateTime",
    "classDateTime",
    "locationName",
    "signedInStatus",
    "staffName",
    "bookingOriginatedFromWaitlist",
    "clientsNumberOfVisitsAtSite",
    "clientPassSessionsTotal",
    "clientPassSessionsRemaining",
    "clientPassActivationDateTime",
    "clientPassExpirationDateTime",
    "itemName",
    "soldByName",
    "totalAmountPaid",
    "saleDateTime",
    "items",
    "status",
    "isConfirmed",
    "hasArrived",
    "staffFirstName",
    "staffLastName",
    "startDateTime",
    "endDateTime",
    "durationMinutes",
    "appointmentName",
  ],
};

function normalizeEventPropertyKey(key: string, provider?: string): string {
  let normalized = key
    .replace(/^properties\./, "")
    .replace(/^mariana_tek\./, "")
    .replace(/^fitdegree\./, "");
  if (provider) {
    normalized = normalized.replace(new RegExp(`^${provider.replace("-", "_")}\\.`), "");
  }
  return normalized;
}

function isInternalEventProperty(key: string): boolean {
  const lower = key.toLowerCase();
  return EVENT_PROPERTY_INTERNAL_TOKENS.some((token) => lower.includes(token));
}

function isIdentifierEventProperty(key: string): boolean {
  const raw = normalizeEventPropertyKey(key);
  const normalized = raw.toLowerCase();
  return (
    normalized === "id" ||
    normalized === "identifier" ||
    normalized.endsWith(".id") ||
    normalized.endsWith("_id") ||
    normalized.includes("_id_") ||
    normalized.endsWith("identifier") ||
    // Mindbody (and similar) webhooks use camelCase `classId`, `locationId`.
    /[a-z]Id$/.test(raw) ||
    /[a-z]Ids$/.test(raw)
  );
}

function shouldIncludeEventProperty(key: string, provider?: string): boolean {
  if (isInternalEventProperty(key) || isIdentifierEventProperty(key)) return false;
  if (!provider) return true;
  const normalized = normalizeEventPropertyKey(key, provider);
  if (EVENT_PROPERTY_EXCLUDED_KEYS_BY_PROVIDER[provider]?.has(normalized)) return false;
  const labels = EVENT_PROPERTY_LABELS_BY_PROVIDER[provider] ?? {};
  if (labels[normalized]) return true;
  const allowedPrefixes = EVENT_PROPERTY_ALLOWED_PREFIXES_BY_PROVIDER[provider];
  if (!allowedPrefixes) return true;
  return allowedPrefixes.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}.`),
  );
}

export function labelEventProperty(key: string, provider?: string): string {
  const normalized = normalizeEventPropertyKey(key, provider);
  if (normalized === EVENT_OCCURRED_AT_PROPERTY_KEY) return EVENT_OCCURRED_AT_PROPERTY_LABEL;
  const labels = provider ? EVENT_PROPERTY_LABELS_BY_PROVIDER[provider] : undefined;
  const exact = labels?.[normalized];
  if (exact) return exact;

  const suffixMatch = Object.entries(labels ?? {}).find(([suffix]) => normalized.endsWith(suffix));
  if (suffixMatch) return suffixMatch[1];

  return startCase(
    normalized
      .replace(/\bdata\./g, "")
      .replace(/\bdata_/g, "")
      .replace(/datetime/gi, "date time"),
  );
}

export function labelEventPropertyValue(
  key: string,
  raw: unknown,
  provider?: string,
): string | undefined {
  const str = String(raw);
  const normalized = normalizeEventPropertyKey(key, provider);
  return provider
    ? EVENT_PROPERTY_VALUE_LABELS_BY_PROVIDER[provider]?.[normalized]?.[str]
    : undefined;
}

export function customerPropertyOptions(
  props: CustomerProperties,
  opts?: { extraKeys?: Array<{ key: string; type?: PropertyType }> },
): {
  options: ConditionKey[];
  groupedOptions: GroupedOption<ConditionKey>[];
} {
  function groupBySource(conditionKeys: ConditionKey[]): GroupedOption<ConditionKey>[] {
    return Object.values(
      conditionKeys.reduce(
        (grouped, key) => {
          (grouped[key.source] = grouped[key.source] || {
            label: key.source,
            options: [],
          }).options.push(key);
          return grouped;
        },
        {} as Record<string, GroupedOption<ConditionKey>>,
      ),
    );
  }
  const options: ConditionKey[] = [];
  const seen = new Set<string>();
  for (const customerKey of Object.keys(props.properties)) {
    options.push({
      label: parseCustomerPropertyLabel(customerKey),
      description: "",
      value: customerKey,
      source: parseCustomerPropertySource(customerKey),
      type: props.properties[customerKey].type,
    });
    seen.add(customerKey);
  }
  if (opts?.extraKeys?.length) {
    for (const extra of opts.extraKeys) {
      if (!extra.key || seen.has(extra.key)) continue;
      options.push({
        label: parseCustomerPropertyLabel(extra.key),
        description: "",
        value: extra.key,
        source: parseCustomerPropertySource(extra.key),
        type: extra.type ?? "string",
      });
      seen.add(extra.key);
    }
  }
  const groupedOptions = groupBySource(options);
  return {
    groupedOptions,
    options,
  };
}

export function customerEventSourceLabel(source: string): string {
  if (source === "helios-pixel") return "Website";
  return startCase(source);
}

export function customerEventOptions(availableCustomerEvents: AvailableCustomerEvent[]): {
  options: EventKey[];
  groupedOptions: GroupedOption<EventKey>[];
} {
  function groupBySource(keys: EventKey[]): GroupedOption<EventKey>[] {
    return Object.values(
      keys.reduce(
        (grouped, key) => {
          (grouped[key.source] = grouped[key.source] || {
            label: customerEventSourceLabel(key.source),
            options: [],
          }).options.push(key);
          return grouped;
        },
        {} as Record<string, GroupedOption<EventKey>>,
      ),
    );
  }
  const options: EventKey[] = [];
  availableCustomerEvents.forEach((eventGroup: any) => {
    eventGroup.events.forEach((eventKey: any) => {
      options.push({
        label: startCase(eventKey),
        description: "",
        value: eventKey,
        source: eventGroup.type,
        type: eventGroup.type,
      });
    });
  });

  const groupedOptions = groupBySource(options);
  return {
    groupedOptions,
    options,
  };
}

export function customerEventPropertyOptions(
  props: {
    properties: Record<string, { type: PropertyType }>;
  },
  opts?: { provider?: string },
): {
  options: EventPropertyOption[];
} {
  const propertyKeys = Object.keys(props.properties);
  const eligibleKeys = propertyKeys.filter(
    (key) => !isInternalEventProperty(key) && !isIdentifierEventProperty(key),
  );
  const curatedKeys = eligibleKeys.filter((key) => shouldIncludeEventProperty(key, opts?.provider));
  const keysToRender = curatedKeys.length > 0 ? curatedKeys : eligibleKeys;
  const options: EventPropertyOption[] = [];
  for (const propertyKey of keysToRender) {
    options.push({
      label: labelEventProperty(propertyKey, opts?.provider),
      value: propertyKey,
      type: props.properties[propertyKey].type,
    });
  }
  options.sort((a, b) => a.label.localeCompare(b.label));
  return {
    options,
  };
}

export function customerEventPropertyValueOptions(props: { values: "string"[] }): {
  options: EventPropertyOption[];
} {
  const options: EventPropertyOption[] = props.values.map((value) => {
    return {
      label: value,
      value: value,
      type: "string",
    };
  });
  return {
    options,
  };
}

export const ORDINAL_PROPERTIES = new Set([
  "properties.tablelist.birthdayOrdinal",
  "properties.mariana_tek.birth_date_ordinal",
  "properties.fitdegree.birthdate_ordinal",
  "properties.zenoti.date_of_birth_ordinal",
]);
